import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchGeneralNotificationsAPI } from '../services/alertService';

export default function NotificationsPage() {
    const { serverId } = useParams();
    const navigate = useNavigate();
    
    // Strict Pagination States
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [notifications, setNotifications] = useState([]);
    
    const [lastDoc, setLastDoc] = useState(null);
    const [hasMore, setHasMore] = useState(true);

    // Exact Match Search States
    const [searchField, setSearchField] = useState('staffID');
    const [searchValue, setSearchValue] = useState('');
    const [isSearching, setIsSearching] = useState(false);

    const loadNotifications = async (isLoadMore = false) => {
        if (isLoadMore) setLoadingMore(true);
        else setLoading(true);

        const currentCursor = isLoadMore ? lastDoc : null;
        
        const searchConfig = isSearching && searchValue.trim() !== '' 
            ? { field: searchField, value: searchValue } 
            : null;

        const response = await fetchGeneralNotificationsAPI(serverId, currentCursor, searchConfig);
        
        if (isLoadMore) {
            setNotifications(prev => [...prev, ...response.data]);
        } else {
            setNotifications(response.data);
        }

        setLastDoc(response.lastVisible);
        setHasMore(response.hasMore);
        
        setLoading(false);
        setLoadingMore(false);
    };

    useEffect(() => {
        loadNotifications(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serverId, isSearching]);

    // Search Handlers
    const handleExecuteSearch = (e) => {
        e.preventDefault();
        if (searchValue.trim() === '') return;
        setLastDoc(null);
        setIsSearching(true);
    };

    const handleClearSearch = () => {
        setSearchValue('');
        setLastDoc(null);
        setIsSearching(false);
    };

    const parseDate = (val) => {
        if (!val) return new Date();
        if (val.toDate) return val.toDate();
        return new Date(val);
    };

    const renderExtraFields = (item) => {
        const standardKeys = ['id', 'title', 'notification', 'notifiedAt', 'createdAt', 'type', 'serverID', 'staffID', 'body'];
        const extraEntries = Object.entries(item).filter(([key, value]) => {
            return !standardKeys.includes(key) && value !== "" && value !== null && value !== undefined && typeof value !== 'object'; 
        });

        if (extraEntries.length === 0) return null;

        return (
            <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: '1px dashed #e2e8f0', display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                {extraEntries.map(([key, value]) => (
                    <div key={key} style={{ fontSize: '12px', color: '#4a5568', backgroundColor: '#f7fafc', padding: '4px 8px', borderRadius: '4px', border: '1px solid #edf2f7' }}>
                        <span style={{ fontWeight: 'bold', textTransform: 'capitalize', color: '#718096' }}>
                            {key.replace(/([A-Z])/g, ' $1').trim()}:
                        </span> 
                        <span style={{ marginLeft: '5px' }}>{String(value)}</span>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div style={{ padding: '30px', backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '25px' }}>
                <button onClick={() => navigate(-1)} style={{ marginRight: '20px', padding: '8px 16px', border: '1px solid #ddd', borderRadius: '4px', background: 'white', cursor: 'pointer' }}>← Back</button>
                <h1 style={{ margin: 0, color: '#2c3e50', fontSize: '24px' }}>System Notifications</h1>
            </div>

            {/* EXACT MATCH SEARCH BLOCK */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', backgroundColor: 'white', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0', alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 'bold', color: '#4a5568', fontSize: '14px' }}>Exact Search:</span>
                <select 
                    value={searchField} 
                    onChange={(e) => setSearchField(e.target.value)}
                    style={{ padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e0', backgroundColor: '#f7fafc', minWidth: '150px' }}
                    disabled={isSearching}
                >
                    <option value="staffID">Staff ID</option>
                    <option value="type">Notification Type</option>
                </select>
                <form onSubmit={handleExecuteSearch} style={{ display: 'flex', gap: '10px', flex: 1, minWidth: '200px' }}>
                    <input 
                        type="text" 
                        placeholder={`Exact match for ${searchField}...`}
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        disabled={isSearching}
                        style={{ padding: '8px 12px', flex: 1, borderRadius: '4px', border: '1px solid #cbd5e0', backgroundColor: isSearching ? '#f7fafc' : 'white' }}
                    />
                    {!isSearching ? (
                        <button type="submit" style={{ padding: '8px 16px', backgroundColor: '#3182ce', color: 'white', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer' }}>Search</button>
                    ) : (
                        <button type="button" onClick={handleClearSearch} style={{ padding: '8px 16px', backgroundColor: '#e2e8f0', color: '#4a5568', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer' }}>Clear Search</button>
                    )}
                </form>
            </div>

            {loading ? <p>Loading logs...</p> : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    {notifications.length > 0 ? notifications.map(item => (
                        <div key={item.id} style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderLeft: '4px solid #718096' }}>
                            {/* Header: Type & Time */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                <span style={{ fontSize: '11px', backgroundColor: '#edf2f7', padding: '2px 8px', borderRadius: '10px', textTransform: 'uppercase', fontWeight: 'bold', color: '#4a5568' }}>
                                    {item.type ? item.type.replace(/_/g, ' ') : 'System'}
                                </span>
                                <span style={{ fontSize: '12px', color: '#a0aec0' }}>
                                    {parseDate(item.notifiedAt || item.createdAt).toLocaleString()}
                                </span>
                            </div>
                            
                            {/* Main Content */}
                            <h3 style={{ margin: '0 0 5px 0', fontSize: '16px', color: '#2d3748' }}>
                                {item.title || "Notification"}
                            </h3>
                            
                            <p style={{ margin: 0, fontSize: '14px', color: '#718096', lineHeight: '1.5' }}>
                                {item.notification || item.body}
                            </p>
                            
                            {/* Staff ID Badge */}
                            {item.staffID && (
                                <div style={{ fontSize: '12px', color: '#cbd5e0', marginTop: '8px', fontFamily: 'monospace' }}>
                                    Staff ID: {item.staffID}
                                </div>
                            )}

                            {/* DYNAMIC FIELDS RENDERER */}
                            {renderExtraFields(item)}
                        </div>
                    )) : (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#a0aec0', backgroundColor: 'white', borderRadius: '8px' }}>
                            No notifications found matching your criteria.
                        </div>
                    )}
                    {hasMore && notifications.length > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', paddingBottom: '20px' }}>
                            <button 
                                onClick={() => loadNotifications(true)}
                                disabled={loadingMore}
                                style={{ padding: '10px 24px', borderRadius: '4px', border: '1px solid #cbd5e0', backgroundColor: 'white', color: '#4a5568', fontWeight: 'bold', cursor: loadingMore ? 'not-allowed' : 'pointer' }}
                            >
                                {loadingMore ? 'Loading...' : 'Load Next 50'}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}